<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { p1Api, type OrganizationListItem } from '@/lib/p1-api';

const me = ref<Awaited<ReturnType<typeof p1Api.me>>>();
const router = useRouter();
const organizations = ref<OrganizationListItem[]>([]);
const message = ref('正在解析访问范围…');
const canReadFlows = ref(false);
onMounted(async () => {
  try {
    me.value = await p1Api.me();
    if (me.value.mustChangePassword) {
      await router.replace('/change-password');
      return;
    }
    if (me.value.permissions.includes('organization:read'))
      organizations.value = await p1Api.organizations();
    canReadFlows.value = me.value.permissions.includes('master-data:read');
    message.value = me.value.allowedOrganizationIds.length
      ? `已授权 ${me.value.allowedOrganizationIds.length} 个组织`
      : '当前会话数据范围为 NONE；业务数据保持不可见。';
  } catch {
    message.value = '无法读取会话，请返回登录页重新登录。';
  }
});
async function bindWecom() {
  try {
    const { authorizeUrl } = await p1Api.wecomAuthorize(true);
    window.location.assign(authorizeUrl);
  } catch {
    message.value = '企业微信绑定尚未启用或配置不完整。';
  }
}
</script>

<template>
  <main class="admin-shell">
    <header class="admin-header">
      <div>
        <p class="eyebrow">HSL-EMS · P1</p>
        <h1>组织、用户与权限</h1>
      </div>
      <RouterLink to="/login">切换账号</RouterLink>
    </header>
    <button class="secondary-button" type="button" @click="bindWecom">
      绑定企业微信并接收关键消息
    </button>
    <RouterLink v-if="canReadFlows" class="secondary-button admin-action-link" to="/approval-flows">
      审批流程配置与模拟器
    </RouterLink>
    <section class="scope-banner">
      <strong>服务端数据范围</strong>
      <p>{{ message }}</p>
    </section>
    <section class="admin-grid">
      <article>
        <h2>当前角色</h2>
        <div class="tag-list">
          <span v-for="role in me?.roleCodes" :key="role">{{ role }}</span
          ><em v-if="!me?.roleCodes.length">尚无有效角色</em>
        </div>
      </article>
      <article>
        <h2>动作权限</h2>
        <div class="tag-list">
          <span v-for="permission in me?.permissions" :key="permission">{{ permission }}</span
          ><em v-if="!me?.permissions.length">默认拒绝</em>
        </div>
      </article>
    </section>
    <section class="table-card">
      <div>
        <h2>可见组织</h2>
        <p>列表由 API 使用 allowedOrganizationIds 强制裁剪。</p>
      </div>
      <table v-if="organizations.length">
        <thead>
          <tr>
            <th>编码</th>
            <th>名称</th>
            <th>类型</th>
            <th>状态</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="org in organizations" :key="org.id">
            <td>{{ org.code }}</td>
            <td :style="{ paddingLeft: `${18 + org.depth * 18}px` }">{{ org.name }}</td>
            <td>{{ org.type }}</td>
            <td>{{ org.status }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="empty-state">没有可见组织。这是默认拒绝策略的预期结果。</div>
    </section>
  </main>
</template>
